import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  TextField,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation, TFunction } from "react-i18next";
import { useQuery } from '@tanstack/react-query';
import { CarSharingReservation } from "../../types";
import { getReservationHistory, getCarsharingClient } from "../../api/service/customerbaseApi";
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import CarSharingDataGrid from "./CarSharingDataGrid";
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Dayjs } from "dayjs";
import { useCarSharingContext } from './CarSharingContext';

const dateTimeValueFormatter = ({ value }: { value: string | undefined | null }) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('it-IT');
};

const getColumns = (t: TFunction): GridColDef[] => {
  const formatReservationStatus = (params: GridRenderCellParams): string => {
    const row = params.row as CarSharingReservation;
    const state = Number(row.reservationStateId);
    const detailState = Number(row.reservationSubStateId);

    if (state === 0) {
      switch (detailState) {
        case 0: return `${t('csReservations.status.closed', 'Closed')} (${t('csReservations.status.free', 'Free')})`;
        case 1: return t('csReservations.status.vehicleBooked', 'Vehicle Booked');
        case 3: return t('csReservations.status.onTransmitting', 'On Transmitting');
        case 13: return t('csReservations.status.scheduled', 'Scheduled');
        case 14: return t('csReservations.status.onTransmitting', 'On Transmitting');
        case 8: return t('csReservations.status.resendBooking', 'Resend Booking');
        case 18: return `${t('csReservations.status.vehicleNotPickedUp', 'Vehicle Not Picked Up')} (${t('csReservations.status.free', 'Free')})`;
        case 17: return `${t('csReservations.status.pinProblem', 'Pin Problem')} (${t('csReservations.status.free', 'Free')})`;
        case 6: return t('csReservations.status.onDeleting', 'On Deleting');
        case 16: return t('csReservations.status.waitingForResponse', 'Waiting For Response');
        default: return t('csReservations.status.undefined', 'Undefined');
      }
    } else if (state === 1) {
      switch (detailState) {
        case 19: return `${t('csReservations.status.deleted', 'Deleted')} (${t('csReservations.status.free', 'Free')})`;
        default: return t('csReservations.status.undefined', 'Undefined');
      }
    } else if (state === 2) {
      switch (detailState) {
        case 7: return `${t('csReservations.status.failed', 'Failed')} (${t('csReservations.status.free', 'Free')})`;
        default: return t('csReservations.status.undefined', 'Undefined');
      }
    } else if (state === 3) {
      switch (detailState) {
        case 4: return t('csReservations.status.finished', 'Finished');
        case 9: return `${t('csReservations.status.bookingExpired', 'Booking Expired')} (${t('csReservations.status.free', 'Free')})`;
        default: return t('csReservations.status.undefined', 'Undefined');
      }
    } else if (state === 4) {
      switch (detailState) {
        case 10: return t('csReservations.status.onRunning', 'On Running');
        case 12: return t('csReservations.status.onTransmitting', 'On Transmitting');
        default: return t('csReservations.status.undefined', 'Undefined');
      }
    } else {
      return t('csReservations.status.undefined', 'Undefined');
    }
  };

  const formatDistance = (params: GridRenderCellParams): string => {
    const row = params.row as CarSharingReservation;
    const distance = row.reservationDistance;
    const state = Number(row.reservationStateId);
    const detailState = Number(row.reservationSubStateId);

    if (distance != null) return `${distance} Km`;
    if (state === 3 && detailState === 4) return "0 Km";
    return "-";
  };

  return [
    {
      field: 'reservationID',
      headerName: t('csReservations.headers.reservationID'),
      width: 140,
    },
    {
      field: 'reservationStartDate',
      headerName: t('csReservations.headers.reservationStartDate'),
      width: 200,
      valueFormatter: dateTimeValueFormatter,
    },
    {
      field: 'reservationEndDate',
      headerName: t('csReservations.headers.reservationEndDate'),
      width: 200,
      valueFormatter: dateTimeValueFormatter,
    },
    {
      field: 'distance',
      headerName: t('csReservations.headers.distance'),
      width: 120,
      renderCell: formatDistance,
    },
    {
      field: 'carPlate',
      headerName: t('csReservations.headers.carPlate'),
      width: 150,
    },
    {
      field: 'carName',
      headerName: t('csReservations.headers.carName'),
      width: 150,
    },
    {
      field: 'city',
      headerName: t('csReservations.headers.city'),
      width: 150,
    },
    {
      field: 'departureParking',
      headerName: t('csReservations.headers.departureParking'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'arrivalParking',
      headerName: t('csReservations.headers.arrivalParking'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'reservationStateId',
      headerName: t('csReservations.headers.reservationStateId'),
      width: 150,
      renderCell: formatReservationStatus,
    },
    {
      field: 'monitored',
      headerName: t('csReservations.headers.monitored'),
      width: 170,
      valueFormatter: ({ value }) =>
        value === 1 ? t('global.yes', 'Yes') : t('global.no', 'No'),
    },
  ];
};

export default function CustomerBaseCarSharingReservations() {
  const { t } = useTranslation("customerbase");
  const [expanded, setExpanded] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const { personId, contractId, setContractId, csCustomerId, language, contractData } = useCarSharingContext();

  useEffect(() => {
    if (expanded && !hasBeenOpened) setHasBeenOpened(true);
  }, [expanded, hasBeenOpened]);

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({ startDate: '', endDate: '' });
  const [searchPerformed, setSearchPerformed] = useState(false);

  const handleSearch = () => {
    setAppliedFilters({
      startDate: dateFrom ? dateFrom.format('YYYY-MM-DD') : '',
      endDate: dateTo ? dateTo.format('YYYY-MM-DD') : ''
    });
    setSearchPerformed(true);
  };

  const handleDateFromChange = (newValue: Dayjs | null) => {
    setDateFrom(newValue);
    setDateTo(newValue);
  };

  const handleRedirect = async () => {
    if (!csCustomerId) return;
    const params: CarsharingRedirectParams = {
      lang: language || 'it',
      type: 'CUSTOMER',
      section: 'RESERVATIONS',
      id: String(csCustomerId)
    };
    try {
      await redirectToCarsharing(params);
    } catch (error) {
      console.error('Redirect failed:', error);
    }
  };

  const { data: reservations, isError, error, isFetching } = useQuery<CarSharingReservation[]>(
    ['reservationHistory', personId, contractId, appliedFilters.startDate, appliedFilters.endDate, language],
    () =>
      getReservationHistory(
        {
          personId: Number(personId!),
          contractId: Number(contractId!),
          startDate: appliedFilters.startDate || undefined,
          endDate: appliedFilters.endDate || undefined,
        },
        getCarsharingClient(),
        (language || 'it').toUpperCase()
      ),
    { enabled: searchPerformed && !!personId && Number.isFinite(contractId), retry: 1 }
  );

  useEffect(() => {
    if (!contractId && contractData?.length) {
      setContractId?.(contractData[0].contractID);
    }
  }, [contractId, contractData, setContractId]);


  const columns = getColumns(t);
  const reservationsWithId = (reservations || []).map(r => ({ ...r, id: r.reservationID }));

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography sx={{ fontWeight: 500 }}>{t("tabs.accordionsLabels.csReservations")}</Typography>
          {expanded && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                void handleRedirect();
              }}
              disabled={!csCustomerId}
              color="secondary"
            >
              <LinkIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="contract-select-label">
                    {t("csReservations.filters.contract")}
                  </InputLabel>
                  <Select
                    labelId="contract-select-label"
                    id="searchReservationHistoryContractNumberList"
                    value={contractId || ""}
                    label={t("csReservations.filters.contract")}
                    onChange={(e) => setContractId?.(Number(e.target.value))}
                  >
                    {contractData?.map((contract) => {
                      const ownerType = contract.isOwner
                        ? t("csReservations.owner")
                        : t("csReservations.guest");
                      return (
                        <MenuItem key={contract.contractID} value={contract.contractID}>
                          {`${contract.contractNumber} - ${contract.contractTypeName} - ${ownerType}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ normalDate: "DD/MM/YYYY" }}>
                      <DatePicker
                        label={t("csReservations.filters.dateFrom")}
                        value={dateFrom}
                        onChange={handleDateFromChange}
                        minDate={dayjs('2019-01-01')}
                        maxDate={dayjs('2040-12-31')}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ normalDate: "DD/MM/YYYY" }}>
                      <DatePicker
                        label={t("csReservations.filters.dateTo")}
                        value={dateTo}
                        onChange={setDateTo}
                        minDate={dateFrom || dayjs('2019-01-01')}
                        maxDate={dayjs('2040-12-31')}
                        inputFormat="DD/MM/YYYY"
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={isFetching}
                >
                  <SearchIcon />
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              {!searchPerformed ? null : isFetching ? (
                <CircularProgress />
              ) : isError ? (
                <Alert severity="error">{(error as Error)?.message}</Alert>
              ) : (
                <CarSharingDataGrid rows={reservationsWithId} columns={columns} />
              )}
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}